import React from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { checkPathLocaleAll } from "../utils/url-path";

// Head-API-compatible SEO component.
//
// Unlike ../components/seo.js (which wraps everything in react-helmet), this
// renders bare <title>/<meta>/<link> tags so it can be used inside a Gatsby
// `Head` export. The Head API renders each page's head in isolation, which
// eliminates the non-deterministic cross-page metadata bleed that react-helmet
// exhibits under Gatsby 5 parallel SSR + React 19.
//
// The Head render context has no router, so `pathname` must be passed in
// (Gatsby provides `location` as a prop to the Head function).

const DEFAULT_META_IMAGE =
  "https://images.ctfassets.net/nfpsrlop6sws/4huePv7JX8n0dtdtSC1bsO/80e2b8336635bcee33675bb901c93804/later-meta-image.jpg?w=1200&q=80&fm=webp";

const getMetaImageUrl = (image, siteDomain) => {
  if (!image) return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  if (image.startsWith("//")) {
    return `https:${image}`;
  }

  return `${siteDomain}${image.startsWith("/") ? image : `/${image}`}`;
};

const query = graphql`
  query SeoHead {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl
        defaultImage: image
        twitterUsername

        es {
          defaultTitle: title
          defaultDescription: description
          siteUrl
          defaultImage: image
          twitterUsername
        }
      }
    }
  }
`;

// Accepts either a comma-separated string or an array of keywords and
// normalizes it to a single comma-separated string for the meta tag.
const normalizeKeywords = (keywords) => {
  if (!keywords) return null;

  const value = Array.isArray(keywords) ? keywords.join(", ") : keywords;

  return value.trim() || null;
};

const SeoHead = ({
  title,
  description,
  keywords,
  robots,
  canonical,
  schema,
  image,
  contentfulImage,
  pathname = "",
}) => {
  const siteDomain = `https://later.com`;

  const { isSpanish } = checkPathLocaleAll(pathname);

  const { site } = useStaticQuery(query);
  const { defaultTitle, defaultDescription, twitterUsername } =
    isSpanish && site.siteMetadata.es
      ? site.siteMetadata.es
      : site.siteMetadata;

  const metaKeywords = normalizeKeywords(keywords);

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    // For rebrand launch, defaulting to just this static image for all non-blog pages
    image:
      getMetaImageUrl(image || contentfulImage, siteDomain) ||
      DEFAULT_META_IMAGE,
    url: `${siteDomain}${pathname}`,
  };

  // append trailing slash to fit url standard
  let canonicalUrl = canonical || seo.url;
  if (canonicalUrl && canonicalUrl.slice(-1) !== "/") {
    canonicalUrl += "/";
  }

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta name="image" content={seo.image} />
      {seo.url && <meta property="og:url" content={seo.url} />}
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}
      {seo.image && <meta property="og:image" content={seo.image} />}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterUsername && (
        <meta name="twitter:creator" content={twitterUsername} />
      )}
      {seo.title && <meta name="twitter:title" content={seo.title} />}
      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
      <meta
        content="gRRrmpGBESGhQP4ADv1mCb37NjLpWV-HiNwsrrOqK2U"
        name="google-site-verification"
      />
      <meta
        name="facebook-domain-verification"
        content="pekiws49d9jleziapi8r26fqq3tl65"
      />
      {robots && <meta name="robots" content={robots} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <link href="/images/favicons/favicon.ico" rel="icon" type="image/ico" />
      {schema && <script type="application/ld+json">{`${schema}`}</script>}
    </>
  );
};

export default SeoHead;

SeoHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  robots: PropTypes.string,
  canonical: PropTypes.string,
  schema: PropTypes.string,
  image: PropTypes.string,
  contentfulImage: PropTypes.string,
  pathname: PropTypes.string,
};

SeoHead.defaultProps = {
  title: null,
  description: null,
  keywords: null,
  robots: null,
  canonical: null,
  schema: null,
  image: null,
  contentfulImage: null,
  pathname: "",
};
